import { Banner, BlockStack, Box, Button, Card, ChoiceList, Icon, InlineStack, Layout, Page, Pagination, Popover, Text } from '@shopify/polaris';
import { ChevronDownIcon, ChevronUpIcon } from '@shopify/polaris-icons';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getApiErrorMessage } from '../../app/api/error';
import agent, { FitmentImportHistoryItem, ImportHistoryStatus } from '../../app/api/agent';
import ImportHistoryCards from './ImportHistoryCards';

const pageSize = 20;

type ImportHistoryView = Extract<ImportHistoryStatus, 'active' | 'archived'>;

const historyViewOptions = [
    { value: 'active' as ImportHistoryView, label: 'Active' },
    { value: 'archived' as ImportHistoryView, label: 'Archived' },
];

const statusFilterButtonStyle: React.CSSProperties = {
    appearance: 'none',
    alignItems: 'center',
    background: 'transparent',
    border: 0,
    color: '#202223',
    cursor: 'pointer',
    display: 'inline-flex',
    font: 'inherit',
    gap: 6,
    minHeight: 32,
    padding: '4px 2px',
};

const statusFilterLabelStyle: React.CSSProperties = {
    fontSize: 14,
    lineHeight: '20px',
};

const statusFilterIconStyle: React.CSSProperties = {
    height: 16,
    width: 16,
};

const ImportHistoryPage = () => {
    const navigate = useNavigate();
    const [history, setHistory] = useState<FitmentImportHistoryItem[]>([]);
    const [historyViews, setHistoryViews] = useState<ImportHistoryView[]>(['active']);
    const [statusFilterOpen, setStatusFilterOpen] = useState(false);
    const [historyError, setHistoryError] = useState<string | undefined>();
    const [historyErrorTitle, setHistoryErrorTitle] = useState<string | undefined>();
    const [isLoadingHistory, setIsLoadingHistory] = useState(false);
    const [archivingIds, setArchivingIds] = useState<Record<number, boolean>>({});
    const [downloadingIds, setDownloadingIds] = useState<Record<number, boolean>>({});
    const [downloadingReviewIds, setDownloadingReviewIds] = useState<Record<number, boolean>>({});
    const [page, setPage] = useState(1);

    const historyStatus = useMemo<ImportHistoryStatus>(() => {
        const includesActive = historyViews.includes('active');
        const includesArchived = historyViews.includes('archived');

        if (includesActive && includesArchived) {
            return 'all';
        }

        return includesArchived ? 'archived' : 'active';
    }, [historyViews]);

    const loadHistory = useCallback(async () => {
        try {
            setIsLoadingHistory(true);
            setHistoryError(undefined);
            setHistoryErrorTitle(undefined);
            const result = await agent.Imports.history(historyStatus);
            setHistory(result);
        } catch (error: any) {
            setHistoryError(await getApiErrorMessage(error, 'Failed to load import history.'));
        } finally {
            setIsLoadingHistory(false);
        }
    }, [historyStatus]);

    useEffect(() => {
        loadHistory();
    }, [loadHistory]);

    useEffect(() => {
        setPage(1);
    }, [historyStatus]);

    const totalPages = Math.max(1, Math.ceil(history.length / pageSize));

    useEffect(() => {
        if (page > totalPages) {
            setPage(totalPages);
        }
    }, [page, totalPages]);

    const currentItems = useMemo(() => {
        const startIndex = (page - 1) * pageSize;
        return history.slice(startIndex, startIndex + pageSize);
    }, [history, page]);

    const historyTitle = historyStatus === 'all'
        ? 'All imports'
        : historyStatus === 'archived'
            ? 'Archived imports'
            : 'Active imports';
    const emptyHistoryText = historyStatus === 'archived'
        ? 'No archived imports.'
        : historyStatus === 'all'
            ? 'No import history yet.'
            : 'No active imports.';

    const handleHistoryViewsChange = useCallback((value: string[]) => {
        if (value.length === 0) {
            return;
        }

        setHistoryViews(value as ImportHistoryView[]);
    }, []);

    const toggleStatusFilter = useCallback(() => {
        setStatusFilterOpen((current) => !current);
    }, []);

    const closeStatusFilter = useCallback(() => {
        setStatusFilterOpen(false);
    }, []);

    const handleArchive = useCallback(async (queueId: number) => {
        try {
            setHistoryError(undefined);
            setHistoryErrorTitle(undefined);
            setArchivingIds((prev) => ({ ...prev, [queueId]: true }));
            await agent.Imports.archive(queueId);
            setHistory((prev) => prev.filter((item) => item.queueId !== queueId));
        } catch (error: any) {
            setHistoryErrorTitle('Unable to archive history item');
            setHistoryError(await getApiErrorMessage(error, 'Failed to archive import history item.'));
        } finally {
            setArchivingIds((prev) => ({ ...prev, [queueId]: false }));
        }
    }, []);

    const handleDownload = useCallback(async (item: FitmentImportHistoryItem) => {
        try {
            setHistoryError(undefined);
            setHistoryErrorTitle(undefined);
            setDownloadingIds((prev) => ({ ...prev, [item.queueId]: true }));
            const response = await agent.Imports.download(item.queueId);
            const blobUrl = window.URL.createObjectURL(response.data);
            const link = document.createElement('a');
            link.href = blobUrl;
            link.download = item.originalFileName || `import-${item.queueId}`;
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(blobUrl);
        } catch (error: any) {
            setHistoryErrorTitle('Unable to download import file');
            setHistoryError(await getApiErrorMessage(error, 'Failed to download import file.'));
        } finally {
            setDownloadingIds((prev) => ({ ...prev, [item.queueId]: false }));
        }
    }, []);

    const handleDownloadReview = useCallback(async (item: FitmentImportHistoryItem) => {
        try {
            setHistoryError(undefined);
            setHistoryErrorTitle(undefined);
            setDownloadingReviewIds((prev) => ({ ...prev, [item.queueId]: true }));
            const response = await agent.Imports.downloadReview(item.queueId);
            const blobUrl = window.URL.createObjectURL(response.data);
            const link = document.createElement('a');
            const baseName = (item.originalFileName || `import-${item.queueId}`).replace(/\.[^/.]+$/, '');
            link.href = blobUrl;
            link.download = `${baseName}-review.csv`;
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(blobUrl);
        } catch (error: any) {
            setHistoryErrorTitle('Unable to download review file');
            setHistoryError(await getApiErrorMessage(error, 'Failed to download import review file.'));
        } finally {
            setDownloadingReviewIds((prev) => ({ ...prev, [item.queueId]: false }));
        }
    }, []);

    return (
        <Page
            title="Import history"
            backAction={{ content: 'Imports', onAction: () => navigate('/imports') }}
        >
            <Layout>
                <Layout.Section>
                    <Card>
                        <BlockStack gap="300">
                            <InlineStack align="space-between" blockAlign="center" gap="300">
                                <InlineStack gap="300" blockAlign="center" wrap>
                                    <Text as="h2" variant="headingMd">{historyTitle}</Text>
                                    <Popover
                                        active={statusFilterOpen}
                                        activator={(
                                            <button
                                                type="button"
                                                aria-expanded={statusFilterOpen}
                                                aria-haspopup="true"
                                                onClick={toggleStatusFilter}
                                                style={statusFilterButtonStyle}
                                            >
                                                <span style={statusFilterLabelStyle}>Status</span>
                                                <span style={statusFilterIconStyle}>
                                                    <Icon source={statusFilterOpen ? ChevronUpIcon : ChevronDownIcon} />
                                                </span>
                                            </button>
                                        )}
                                        onClose={closeStatusFilter}
                                    >
                                        <Box padding="300" minWidth="180px">
                                            <ChoiceList
                                                title="Import status"
                                                titleHidden
                                                allowMultiple
                                                choices={historyViewOptions}
                                                selected={historyViews}
                                                onChange={handleHistoryViewsChange}
                                            />
                                        </Box>
                                    </Popover>
                                </InlineStack>
                                <Button onClick={loadHistory} loading={isLoadingHistory}>Refresh</Button>
                            </InlineStack>

                            {historyError && (
                                <Banner tone="critical" title={historyErrorTitle || 'Unable to load import history'}>
                                    <p>{historyError}</p>
                                </Banner>
                            )}

                            {history.length === 0 && !isLoadingHistory ? (
                                <Text as="p" tone="subdued">{emptyHistoryText}</Text>
                            ) : (
                                <>
                                    <ImportHistoryCards
                                        items={currentItems}
                                        archivingIds={archivingIds}
                                        downloadingIds={downloadingIds}
                                        downloadingReviewIds={downloadingReviewIds}
                                        onArchive={handleArchive}
                                        onDownload={handleDownload}
                                        onDownloadReview={handleDownloadReview}
                                    />
                                    <Box paddingBlockStart="200">
                                        <InlineStack align="center">
                                            <Pagination
                                                hasPrevious={page > 1}
                                                onPrevious={() => setPage((prev) => Math.max(1, prev - 1))}
                                                hasNext={page < totalPages}
                                                onNext={() => setPage((prev) => Math.min(totalPages, prev + 1))}
                                            />
                                        </InlineStack>
                                    </Box>
                                </>
                            )}
                        </BlockStack>
                    </Card>
                </Layout.Section>
            </Layout>
        </Page>
    );
};

export default ImportHistoryPage;
